import React, { ChangeEvent, FormEvent } from "react";
import ReactModal from "react-modal";

import ProgressBar from "./ProgressBar";

import { V2 } from "../Api";
import { formatSize } from "../Utilities";

interface IFileUploadDialogProps {
    isOpen: boolean;
    preventUpload?: boolean;

    accept?: string;
    closeOnSuccess?: boolean;
    descriptionOverride?: string;
    endpoint?: string;
    allowCompression?: boolean;
    overrideCompression?: "" | "image";
    title?: string;

    filesOverride?: FileList;
    extraData?: { [key: string]: string };

    allowMultiple?: boolean;
    showProgress?: boolean;

    onRequestClose?: (event?: (React.MouseEvent | React.KeyboardEvent)) => void;
    onUploadSuccess?:
        ((ids: string[]) => void | PromiseLike<void>) |
        ((ids: string[], serverResponses: any[]) => void | PromiseLike<void>);
}

interface IFileUploadDialogState {
    isUploading: boolean | string;
    canUpload: boolean;
    selectedTotalSize: number;
    uploadIndex: number;
    uploadCount: number;
    uploadPercent: number;
    uploadSize: number;

    description: string;
    compress: string;

    uploadSuccess: boolean;
    files: FileList | null;

    [name: string]: any;
}

class FileUploadDialog extends React.Component<IFileUploadDialogProps, IFileUploadDialogState> {
    private fileRef: React.RefObject<HTMLInputElement> = React.createRef();

    constructor(props: Readonly<IFileUploadDialogProps>) {
        super(props);

        const state: IFileUploadDialogState = {
            isUploading: false,
            canUpload: !!props.filesOverride,
            selectedTotalSize: 0,
            uploadIndex: 0,
            uploadCount: 1,
            uploadPercent: 0,
            uploadSize: 0,

            description: "",
            compress: "",

            uploadSuccess: false,
            files: null,
        };

        this.state = state;

        this.handleFormSubmit = this.handleFormSubmit.bind(this);
        this.handleFileChanged = this.handleFileChanged.bind(this);
        this.handleElementChanged = this.handleElementChanged.bind(this);
        this.requestClose = this.requestClose.bind(this);
    }

    public componentDidUpdate(prevProps: Readonly<IFileUploadDialogProps>) {
        if (this.props.isOpen !== prevProps.isOpen && !this.props.isOpen) {
            this.setState({
                description: "",
                uploadSuccess: false,
            });
        }

        if (this.props.filesOverride !== prevProps.filesOverride) {
            this.fileChanged(this.props.filesOverride || null);
        }
    }

    public render() {
        if (!this.props.isOpen) {
            return null;
        }

        return (
            <ReactModal
                isOpen={this.props.isOpen} onRequestClose={this.requestClose}
                overlayClassName="modal-overlay" className="modal-content" portalClassName="modal-portal"
                bodyOpenClassName="modal-open"
                contentLabel={this.props.title || "File Upload"}>
                <form className="input-form" onSubmit={this.handleFormSubmit}>
                    <div className="info-block">
                        <header>
                            <h3>{this.props.title || "File Upload"}</h3>
                        </header>

                        <section>
                            <div className="input-form-group">
                                <label htmlFor="input">File</label>
                                {this.props.filesOverride ? this.props.filesOverride[0].name : <input ref={this.fileRef} type="file" name="input" multiple={this.props.allowMultiple} onChange={this.handleFileChanged} accept={this.props.accept} />}
                                <small>Total file size: {formatSize(this.state.selectedTotalSize || 0)}</small>
                            </div>
                        </section>
                        {
                            !this.props.descriptionOverride
                                ? <section>
                                    <div className="input-form-group">
                                        <label htmlFor="description">Description</label>
                                        <input type="text" name="description" value={this.state.description} onChange={this.handleElementChanged} />
                                    </div>
                                </section>
                                : null
                        }
                        {
                            this.props.allowCompression && !this.props.overrideCompression
                                ? <section>
                                    <div className="input-form-group">
                                        <label htmlFor="compress">Recompression</label>
                                        <select name="compress" value={this.state.compress} onChange={this.handleElementChanged}>
                                            <option value="">Do not recompress</option>
                                            <option value="image">Recompress image (1500px default)</option>
                                        </select>
                                    </div>
                                </section>
                                : null
                        }
                        {this.props.children}
                        &nbsp;
                        {this.state.uploadSuccess ? <div className="success-box"><i className="icon-check" />&nbsp;Upload successful</div> : undefined}
                        {this.state.isUploading === true
                            ? this.props.showProgress === false
                                ? <div className="spinner-outer-text"><span className="spinner lds-dual-ring" />&nbsp;Uploading...</div>
                                : <div>
                                    <span>Uploading {this.state.uploadIndex + 1} of {this.state.uploadCount} <small>({formatSize(this.state.uploadSize, 0)})</small>... {this.state.uploadPercent.toFixed(0)} %</span>
                                    <ProgressBar className="progress-contained" value={this.state.uploadPercent} />
                                </div>
                            : typeof this.state.isUploading === "string"
                                ? <span className="error-text">{this.state.isUploading}</span>
                                : undefined
                        }

                        <div className="action-panel-dialog">
                            <button type="submit" className="button green push-right text-with-icon" disabled={this.props.preventUpload || !this.state.canUpload || this.state.isUploading === true}><i className="icon-upload" /><span>Upload</span></button>
                            <button type="button" className="button" disabled={this.state.isUploading === true} onClick={this.requestClose}>Close</button>
                        </div>
                    </div>
                </form>
            </ReactModal>
        );
    }

    private handleElementChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) {
        const name = e.currentTarget.name;
        const value = e.currentTarget.value;

        this.setState({
            [name]: value
        });
    }

    private handleFileChanged(e: ChangeEvent<HTMLInputElement>) {
        this.fileChanged(e.currentTarget.files);
    }

    private fileChanged(files: FileList | null) {
        let totalSize = 0;
        if (files) {
            for (let i = 0; i < files.length; i++) {
                totalSize += files[i].size;
            }
        }

        this.setState({
            canUpload: !!files && files.length > 0,
            description: files ? files![0].name : "",
            selectedTotalSize: totalSize,
            files
        });
    }

    private requestClose(e?: (React.MouseEvent | React.KeyboardEvent)) {
        this.setState({
            selectedTotalSize: 0,
            description: "",
            canUpload: false,
            files: null
        });

        if (this.state.isUploading !== true && this.props.onRequestClose) {
            this.props.onRequestClose(e);
        }
    }

    private handleFormSubmit(e: FormEvent) {
        e.preventDefault();

        this.uploadFiles();
    }

    private async uploadFiles() {
        if (!this.state.files) {
            return;
        }

        try {
            const fileList = this.state.files || [];

            this.setState({
                isUploading: true,
                uploadCount: fileList.length
            });

            let ids: string[] = [];
            let responses: any[] = [];
            for (let i = 0; i < fileList.length; i++) {
                this.setState({
                    isUploading: true,
                    uploadIndex: i,
                    uploadPercent: 0,
                    uploadSize: fileList[i].size,
                });

                const fd = new FormData();
                fd.append("", fileList[i]);
                fd.append("description", this.props.descriptionOverride || this.state.description);
                if (this.props.extraData) {
                    for (const key of Object.keys(this.props.extraData)) {
                        fd.append(key, this.props.extraData[key]);
                    }
                }
                const compress = this.props.overrideCompression || this.state.compress;
                const endpoint = this.props.endpoint || (compress ? `files?compress=${compress}` : "files");
                const result = await V2.post<any>(endpoint, fd, (current, total) => {
                    this.setState({
                        uploadPercent: (current / total) * 100
                    });
                });
                if (result && result.id) {
                    ids.push(result.id);
                }
                if (result) {
                    responses.push(result);
                }
            }

            // Clear file list
            if (this.fileRef.current) {
                this.fileRef.current.value = "";
            }

            if (this.props.onUploadSuccess) {
                const successResult = this.props.onUploadSuccess(ids, responses) as any;
                if (successResult && 'then' in successResult) {
                    await successResult;
                }
            }

            this.setState({
                isUploading: false,
                uploadSuccess: true,
                description: "",
                canUpload: false,
                files: null
            }, () => {
                if (this.props.closeOnSuccess) {
                    this.requestClose();
                }
            });
        }
        catch (e) {
            this.setState({
                isUploading: e.message || e || "Could not upload file",
            });
        }
    }
}

export default FileUploadDialog;
